import { memo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EarthLoader } from '../../components/loaders/EarthLoader';
import { PageContent } from '../../components/page/PageContent';
import { PageHeader } from '../../components/page/PageHeader';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { HowItWorks } from '../../modules/organisation/referrals/HowItWorks';
import { PayoutHistory } from '../../modules/organisation/referrals/PayoutHistory';
import { ReferralHero } from '../../modules/organisation/referrals/ReferralHero';
import { ReferralLinkCard } from '../../modules/organisation/referrals/ReferralLinkCard';
import { ReferralStats } from '../../modules/organisation/referrals/ReferralStats';
import { ReferredOrganisationsList } from '../../modules/organisation/referrals/ReferredOrganisationsList';
import { T, useT } from '../../translation/src';
import { OrganisationReferralOverviewDocument, OrganisationReferralOverviewQuery, useOrganisationReferralLinkGenerateMutation, useOrganisationReferralOverviewQuery } from '../../types/graphqlTypes';
import { isPartnerRole, ordersForCustomers } from '../../modules/organisation/referrals/partnerRoles';
import { SalesHistory } from '../../modules/organisation/referrals/SalesHistory';

const Referrals_: React.FC2 = () => {
  const { organisationId = '' } = useParams();
  const navigate = useNavigate();
  const { data, loading } = useOrganisationReferralOverviewQuery({ variables: { organisationId }, fetchPolicy: 'cache-and-network' });
  // ReferralOverview has no id, so Apollo cannot merge the mutation's answer into the cached query by itself:
  // the answer is written over the query's entry, and the page shows the link at once.
  const [generate, { loading: generating }] = useOrganisationReferralLinkGenerateMutation({
    update: (cache, { data: generated }) => {
      if (!generated) return;
      cache.writeQuery<OrganisationReferralOverviewQuery>({ query: OrganisationReferralOverviewDocument, variables: { organisationId }, data: { organisationReferralOverview: generated.organisationReferralLinkGenerate } });
    },
  });
  const { showToast } = useToastNotifications();
  const failed = useT('could not make the referral link', { swc: true });
  const overview = data?.organisationReferralOverview;
  // A reseller or full-service partner brings customers in and orders for them; to it the page is about them,
  // and the customer center is theirs.
  const partner = ordersForCustomers(overview?.role);

  const onGenerate = useCallback(async () => {
    try {
      await generate({ variables: { organisationId } });
    } catch (e) {
      showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
    }
  }, [failed, generate, organisationId, showToast]);

  return (
    <PageContent>
      <PageHeader title={isPartnerRole(overview?.role) ? <T _str="partner program" swc /> : <T _str="referral program" swc />} />
      <div className="w-full min-h-0 flex-1 overflow-y-auto">
        {loading && !overview ? (
          <EarthLoader />
        ) : (
          overview && (
            <div className="flex flex-col w-full max-w-6xl gap-6 pb-10">
              <ReferralHero overview={overview} />
              <ReferralLinkCard token={overview.token} onGenerate={onGenerate} generating={generating} partner={isPartnerRole(overview.role)} />
              <ReferralStats overview={overview} partner={isPartnerRole(overview.role)} />
              {isPartnerRole(overview.role) && (
                <>
                  {partner && <ReferredOrganisationsList organisations={overview.referredOrganisations} partner={partner} />}
                  <SalesHistory sales={overview.sales} role={overview.role} onManage={() => navigate(`/organisations/${organisationId}/referrals/sales`)} />
                  {/* A reseller's or full-service partner's margin comes off its invoices: it has no payouts. */}
                  {!partner && <PayoutHistory organisationId={organisationId} payouts={overview.payouts} />}
                </>
              )}
              <HowItWorks role={overview.role} />
            </div>
          )
        )}
      </div>
    </PageContent>
  );
};
export const Referrals = memo(Referrals_);
